import { gql, useQuery } from '@apollo/client';
import { useUserId } from '../../../hooks/useCurrentUser/useCurrentUser';
import { mergeUniqueItems } from '@admiin-com/ds-common';
import {
  ModelSortDirection,
  searchTasks,
  SearchTasksSortBy,
  Task,
  TaskSearchStatus,
  TaskSearchType,
} from '@admiin-com/ds-graphql';
import { useSearchParams } from 'react-router-dom';
import React from 'react';
import { convertFromBackendTask } from '../../../helpers/tasks';

export default function useSearchTasks({
  entityId,
  contactId,
  type = TaskSearchType.ALL,
  status,
  search,
  myTasks,
  dueAtStart,
  dueAtEnd,
  firmId,
  limit,
  sortBy,
  sortDirection,
  disableAutoFetch = false,
  bindingId,
  skipCondition = false,
}: {
  entityId?: string;
  contactId?: string | null;
  type?: TaskSearchType;
  status?: TaskSearchStatus | 'ALL' | null;
  search?: string | null;
  myTasks?: boolean | null;
  dueAtStart?: string | null;
  dueAtEnd?: string | null;
  firmId?: string | null;
  limit?: number | null;
  sortBy?: SearchTasksSortBy | null;
  sortDirection?: ModelSortDirection | null;
  disableAutoFetch?: boolean;
  bindingId?: string;
  skipCondition?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = useUserId();
  const { data, loading, fetchMore, refetch } = useQuery(gql(searchTasks), {
    notifyOnNetworkStatusChange: true,
    variables: {
      status: status === 'ALL' ? null : status, // Convert 'ALL' to null only when querying
      type: type,
      // Other variables remain the same
      searchName: search,
      createdBy: myTasks ? userId : undefined,
      limit,
      entityId,
      contactId,
      dueAtStart,
      dueAtEnd,
      firmId,
      sortBy,
      sortDirection,
      bindingId,
    },
    fetchPolicy: disableAutoFetch ? 'cache-first' : 'cache-and-network',
    skip: (!entityId && !firmId && !contactId) || skipCondition,
  });
  const tasks = React.useMemo(() => data?.searchTasks?.items || [], [data]);
  const totalFromServer = React.useMemo(
    () => data?.searchTasks?.total || 0,
    [data]
  );
  const hasNextPage = React.useMemo(
    () => totalFromServer > (tasks?.length ?? 0),
    [totalFromServer, tasks]
  );

  const handleLoadMore = async () => {
    if (!hasNextPage || loading) return;
    await fetchMore({
      variables: {
        from: tasks?.length,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;
        const newTasks = mergeUniqueItems(
          prevResult.searchTasks?.items ?? [],
          fetchMoreResult.searchTasks?.items ?? [],
          ['id'] // Assuming 'id' is the unique key
        );

        const updatedParams = new URLSearchParams(searchParams.toString());
        updatedParams.set('tasks', newTasks?.length.toString() ?? '20');
        setSearchParams(updatedParams);

        return {
          ...fetchMoreResult,
          searchTasks: {
            ...fetchMoreResult.searchTasks,
            items: newTasks,
          },
        };
      },
    });
  };

  const { convertedTasks, filteredTotal } = React.useMemo(() => {
    // let filteredTasks = tasks;
    // let total = totalFromServer;

    // Filter out ATO entity tasks when searching for documents/esignature
    // if (type === TaskSearchType.ESIGNATURE || type === TaskSearchType.DOCUMENTS) {
    //   filteredTasks = tasks.filter((task: Task) => {
    //     return task.category !== TaskCategory.TAX;
    //   });
    //
    //   // Update total to reflect filtered count
    //   total = filteredTasks.length;
    // }

    return {
      convertedTasks:
        tasks?.map((task: Task) => convertFromBackendTask(task)) ?? [],
      filteredTotal: totalFromServer,
    };
  }, [tasks, type, totalFromServer]);

  return {
    tasks: convertedTasks,
    loading,
    handleLoadMore,
    total: filteredTotal,
    refetch,
    hasNextPage,
  };
}
